import React, { useRef, Children } from 'react';
import { TrendingUpIcon } from 'lucide-react';
import { AnimatedMetric } from '../ui/AnimatedMetric';
import { motion, useScroll, useTransform } from 'framer-motion';
export function ResultsSnapshot() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  // Subtle rotation for cards based on scroll
  const rotate1 = useTransform(scrollYProgress, [0, 0.5, 1], [2, 0, -2]);
  const rotate2 = useTransform(scrollYProgress, [0, 0.5, 1], [-2, 0, 2]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.95, 1, 1, 0.95]);
  const results = [{
    headline: 'SCALABILITY UNLOCK',
    industry: 'Life Insurance',
    location: 'Multi-Market',
    interviews: 144,
    hires: 101,
    metric: {
      label: '70% Hire Rate',
      value: 70,
      suffix: '%'
    }
  }, {
    headline: 'REVENUE JUMP',
    industry: 'Windows',
    location: 'Western US',
    interviews: 388,
    hires: 250,
    metric: {
      label: 'New Revenue',
      value: 2,
      prefix: '$',
      suffix: 'M+'
    }
  }, {
    headline: 'ROOKIE RAMP-UP',
    industry: 'Roofing',
    location: 'Atlanta',
    interviews: 86,
    hires: 57,
    metric: {
      label: '66% Conversion',
      value: 66,
      suffix: '%'
    }
  }, {
    headline: 'RETENTION FIX',
    industry: 'Solar',
    location: 'Southwest',
    interviews: 215,
    hires: 142,
    metric: {
      label: '78% Retention',
      value: 78,
      suffix: '%'
    }
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      rotateX: 10
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section ref={sectionRef} className="section-padding bg-gray-50 overflow-hidden" data-id="element-902">
      <div className="container-custom" data-id="element-903">
        <motion.div className="max-w-3xl mx-auto text-center mb-12 md:mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} data-id="element-904">
          <motion.div className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-mint/10 rounded mb-4 md:mb-6" style={{
          scale
        }} data-id="element-905">
            <TrendingUpIcon className="h-6 w-6 md:h-7 md:w-7 text-mint" data-id="element-906" />
          </motion.div>
          <h2 className="mb-4 md:mb-6" data-id="element-907">Math. Not Magic.</h2>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-908">
            Real companies. Real numbers. This is what happens when you install
            the HIRE Advantage.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 lg:gap-8 max-w-7xl mx-auto perspective-1000" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-909">
          {results.map((result, index) => <motion.div key={index} className="bg-white rounded-xl p-4 md:p-6 lg:p-8 shadow-lg border-2 border-gray-200 hover:border-mint transition-all hover:shadow-xl" variants={itemVariants} style={{
          rotateY: index % 2 === 0 ? rotate1 : rotate2,
          transformStyle: 'preserve-3d'
        }} whileHover={{
          scale: 1.02,
          rotateY: 0,
          transition: {
            duration: 0.2
          }
        }} data-id="element-910">
              <div className="mb-4 md:mb-6" data-id="element-911">
                <div className="text-xs md:text-sm font-black text-mint uppercase tracking-wide mb-2 md:mb-3" data-id="element-912">
                  {result.headline}
                </div>
                <div className="inline-block bg-mint/10 text-mint px-2 py-0.5 md:px-3 md:py-1 rounded text-xs md:text-sm font-bold mb-2 md:mb-3" data-id="element-913">
                  {result.industry}
                </div>
                <p className="text-gray-600 text-xs md:text-sm" data-id="element-914">
                  {result.location}
                </p>
              </div>

              <div className="space-y-2 md:space-y-3 lg:space-y-4 mb-4 md:mb-6" data-id="element-915">
                <div className="flex justify-between items-center pb-2 md:pb-3 border-b border-gray-200" data-id="element-916">
                  <span className="text-gray-600 text-xs md:text-sm lg:text-base" data-id="element-917">
                    Interviews
                  </span>
                  <span className="text-lg md:text-xl lg:text-2xl font-bold text-deep-blue" data-id="element-918">
                    {result.interviews}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 md:pb-3 border-b border-gray-200" data-id="element-919">
                  <span className="text-gray-600 text-xs md:text-sm lg:text-base" data-id="element-920">
                    Hires
                  </span>
                  <span className="text-lg md:text-xl lg:text-2xl font-bold text-deep-blue" data-id="element-921">
                    {result.hires}
                    {result.hires === 250 && '+'}
                  </span>
                </div>
              </div>

              <div className="bg-mint/5 rounded-lg p-3 md:p-4 text-center border border-mint/20" data-id="element-922">
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-mint mb-1" data-id="element-923">
                  <AnimatedMetric value={result.metric.value} prefix={result.metric.prefix} suffix={result.metric.suffix} duration={1500} data-id="element-924" />
                </div>
                <p className="text-xs md:text-sm text-gray-600 font-medium" data-id="element-925">
                  {result.metric.label}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        <motion.div className="mt-8 md:mt-12 text-center" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.4
      }} data-id="element-926">
          <a href="/case-studies" className="inline-block text-mint font-semibold hover:text-mint-dark transition-colors text-sm md:text-base" data-id="element-927">
            See The Case Studies →
          </a>
        </motion.div>
      </div>
    </section>;
}